// Copy drafted by AI; reviewed and approved by the user 2026-10-13 (spec 160).
/** The words of Admin → Linked accounts (spec 160, contracts/ui.md). */
export const LINKED_ACCOUNTS_COPY = {
  policyOn: (name: string) => `${name} now requires personal accounts.`,
  policyOff: (name: string) => `${name} no longer requires personal accounts.`,
  policyNotChanged: (name: string) => `Could not change ${name}. Try again.`,
  policyFailed: 'The policy could not be saved.',
  policiesFailed: 'The workspaces could not be loaded.',

  search: 'Search people',
  person: 'Person',
  notLinked: 'Not linked',
  expires: (date: string) => `expires on ${date}`,
  remove: 'Remove',
  removeTitle: (person: string, forge: string) =>
    `Remove ${person}'s ${forge} account?`,
  removeBody: (host: string) =>
    `Their runs will no longer act as them on ${host}. In workspaces that require personal accounts, their runs will be blocked until they link it again. They will be told.`,
  removePending: 'Removing…',
  removed: (person: string, forge: string) =>
    `${person}'s ${forge} account removed. They have been told.`,
  removeFailed: 'The account could not be removed.',
  noPeople: 'No people yet.',
  peopleFailed: 'People could not be loaded.',
  loading: 'Loading…',
} as const;

// Copy drafted by AI; reviewed and approved by the user 2026-10-13 (spec 161).
/** Setting up sign-in on Admin → Linked accounts (spec 161, contracts/ui.md). */
export const FORGE_APPS_COPY = {
  heading: 'Sign in with GitLab or GitHub',
  intro:
    "Let people link their account by signing in on GitLab or GitHub instead of pasting a token. Each host needs an app you create there once. The platform then renews people's access by itself.",
  unavailable:
    "Sign-in linking needs the platform's public address, and none is set for this installation. People can still link with a personal access token.",
  redirectLabel: 'Redirect address',
  copyRedirect: 'Copy redirect address',
  copied: 'Copied',
  setUp: 'Sign-in set up',
  notSetUp: 'Not set up',
  clientId: (id: string) => `client ID ${id}`,
  changed: (date: string) => `changed on ${date}`,
  setUpAction: 'Set up',
  change: 'Change',
  remove: 'Remove',
  setUpLabel: (host: string) => `Set up sign-in for ${host}`,
  changeLabel: (host: string) => `Change sign-in for ${host}`,
  removeLabel: (host: string) => `Remove sign-in for ${host}`,
  noHosts: 'No workspace connects to GitLab or GitHub yet.',
  loadFailed: 'Sign-in setup could not be loaded.',
  keepSecret: 'Leave empty to keep the current secret.',
  showSecret: 'Show secret',
  hideSecret: 'Hide secret',
  save: 'Save',
  saving: 'Saving…',
  cancel: 'Cancel',
  openForge: (host: string) => `Open ${host}`,
  saved: (host: string) => `Sign-in set up for ${host}.`,
  updated: (host: string) => `Sign-in updated for ${host}.`,
  hostGone: (host: string) => `No workspace connects to ${host} any more.`,
  saveFailed: (host: string) =>
    `Could not save sign-in for ${host}. Try again.`,
  removeTitle: (host: string) => `Remove sign-in for ${host}?`,
  removeBody: (host: string) =>
    `People will no longer be able to connect by signing in on ${host}. Accounts already linked this way stop working when their current access runs out, and their owners will be told to link again. Links made with a token are not affected.`,
  removePending: 'Removing…',
  removed: (host: string) => `Sign-in removed for ${host}.`,
  removeFailed: (host: string) =>
    `Could not remove sign-in for ${host}. Try again.`,
} as const;

// Copy drafted by AI; approved by the user 2026-10-13 ("Keep going", spec 162).
/** Admin → Commit identity: the frame and the people list (spec 162, contracts/ui.md). */
export const COMMIT_IDENTITY_COPY = {
  nav: 'Commit identity',
  title: 'Commit identity',
  description:
    'Who pushes as themselves on GitLab and GitHub, and what is stopping the rest.',
  people: 'People',
  shown: (shown: number, total: number) =>
    shown === total ? `${total} people` : `${shown} of ${total} people`,
  filtersLabel: 'Show',
  filters: {
    all: 'All',
    missing: 'Missing a host',
    broken: 'Broken',
    expiring: 'Expiring',
    'signed-in': 'Signed in',
    token: 'Token',
  },
  host: 'Host',
  unusedColumn: 'no workspace uses it',
  showUnused: (count: number) =>
    count === 1
      ? 'Show 1 host no workspace uses'
      : `Show ${count} hosts no workspace uses`,
  hideUnused: 'Hide hosts no workspace uses',
  actions: 'Actions',
  rowMenu: (person: string) => `Actions for ${person}`,
  removeItem: (forge: string, host: string) => `Remove ${forge} link (${host})`,
  nobodyMatches: 'Nobody matches.',
  showEveryone: 'Show everyone',
  noHosts: 'No workspace connects to GitLab or GitHub yet.',
  loading: 'Loading people…',
  /** Days left on a token about to end, beside its dot. */
  daysLeft: (days: number) => `${Math.max(days, 0)}d`,
} as const;

// Copy drafted by AI; reviewed and approved by the user 2026-10-14 (spec 164).
/** Admin → Commit identity: the answer and the queue (spec 164, contracts/ui.md). */
export const COMMIT_SUMMARY_COPY = {
  /** The band's answer, as large figures and words. */
  answer: (themselves: number, total: number) => `${themselves} of ${total}`,
  answerWords: 'push as themselves',
  /** The ring, as one sentence for assistive technology. */
  ringLabel: (themselves: number, total: number) =>
    `${themselves} of ${total} people push as themselves`,
  noHosts: 'No workspace connects to GitLab or GitHub yet.',
  noPeople: 'No people yet.',
  legendLabel: 'Coverage',
  buckets: {
    themselves: 'Push as themselves',
    partly: 'Linked on some hosts',
    relink: 'Need to relink',
    'not-linked': 'No account',
  },
  showBucket: (bucket: string) => `Show who is ${bucket.toLowerCase()}`,
  since: (joined: number, broke: number) =>
    [
      joined > 0
        ? `${joined} more ${joined === 1 ? 'pushes' : 'push'} as themselves`
        : null,
      broke > 0 ? `${broke} ${broke === 1 ? 'link' : 'links'} broke` : null,
    ]
      .filter(Boolean)
      .join(' · '),
  sinceLead: 'Since your last visit:',

  needsALook: 'Needs a look',
  allClear: 'Nothing needs a look.',
  blocked: (person: string, where: string) =>
    `${person} can't start runs in ${where}`,
  workspaces: (count: number) =>
    count === 1 ? '1 workspace' : `${count} workspaces`,
  blockedWhy: (forge: string, host: string, linked: boolean) =>
    linked
      ? `Their ${forge} link on ${host} doesn't work`
      : `No ${forge} account linked on ${host}`,
  broken: (person: string, forge: string) =>
    `${person}'s ${forge} link stopped working`,
  brokenWhy: (host: string, status: string) =>
    `${host} · ${status} · runs use the shared account`,
  expiring: (person: string, forge: string, days: number | null) =>
    `${person}'s ${forge} token ${
      days === null || days <= 0
        ? 'expires today'
        : days === 1
          ? 'expires tomorrow'
          : `expires in ${days} days`
    }`,
  expiringWhy: (host: string) => `${host} · personal token`,
  signIn: (host: string) => `Sign-in isn't set up for ${host}`,
  signInWhy: 'People there can only link by pasting a token.',
  setUp: 'Set up',
  remind: 'Remind',
  remindLabel: (person: string, host: string) =>
    `Remind ${person} about ${host}`,
  remindedAgo: (span: string) => `Reminded ${span} ago`,
  reminder: 'Reminder',
  heldSaid: (person: string, seconds: number) =>
    `Reminder for ${person} in ${seconds} seconds. Undo is available.`,
  undone: 'Reminder undone.',
  tooSoon: 'Already reminded today.',
  notNeeded: 'Their link works now.',
  remindFailed: "Couldn't send the reminder. Try again.",
  reminded: (person: string) => `${person} has been reminded.`,
  showAll: (count: number) => `Show all ${count}`,
  showFewer: 'Show fewer',
} as const;

// Copy drafted by AI; reviewed and approved by the user 2026-10-14 (spec 165).
/** Admin → Commit identity: the policy rail and the sign-in sheet (spec 165). */
export const POLICY_RAIL_COPY = {
  requiredIn: 'Required in',
  requiredIntro:
    "In these workspaces, runs that act for a person use only that person's own account.",
  noneRequired:
    'No workspace requires personal accounts. Runs fall back to the shared account.',
  stops: (count: number) =>
    count === 0
      ? 'Everyone can start runs'
      : count === 1
        ? "1 person can't start runs"
        : `${count} people can't start runs`,
  noForge: "Doesn't connect to GitLab or GitHub",
  /** The same, as a tag beside a workspace's name in the sheet. */
  noForgeTag: 'no GitLab or GitHub',
  stopRequiring: 'Stop requiring',
  stopRequiringLabel: (name: string) =>
    `Stop requiring personal accounts in ${name}`,
  requireIn: 'Require in a workspace',

  sheetTitle: 'Require personal accounts',
  sheetDescription:
    "Pick a workspace. You'll see who it would stop before anything changes.",
  search: 'Search workspaces',
  wouldStop: (count: number) =>
    count === 0 ? 'stops nobody' : `stops ${count}`,
  nothingToRequire: 'Every workspace already requires personal accounts.',
  noMatch: 'No workspace matches.',
  previewStops: (name: string, count: number, total: number, names: string) =>
    `Requiring personal accounts in ${name} would stop ${count} of ${total} people from starting runs there until they link: ${names}.`,
  previewNobody: (name: string) =>
    `Everyone already has a working account for ${name}'s hosts, so this stops nobody.`,
  previewNoForge: (name: string) =>
    `${name} doesn't connect to GitLab or GitHub, so this changes nothing yet.`,
  require: 'Require',
  requiring: 'Saving…',
  cancel: 'Cancel',

  signIn: 'Sign in with GitLab or GitHub',
  signInCount: (configured: number, total: number) =>
    `Set up on ${configured} of ${total} ${total === 1 ? 'host' : 'hosts'}`,
  signInUnavailable: 'Unavailable: no public address is set.',
  manageSignIn: 'Manage sign-in',
} as const;

// Copy drafted by AI; reviewed and approved by the user 2026-10-14 (spec 169).
/** Commit identity, after the console review (spec 169). */
export const COMMIT_FIXES_COPY = {
  shared: (username: string, count: number, host: string) =>
    `@${username} is linked by ${count} people on ${host}`,
  sharedWhy: (names: string) =>
    `${names}. What runs push for each of them carries this one account's name. Ask each to link their own.`,
  nobodyYet: 'Nobody pushes as themselves yet.',
  nobodyNext:
    'Set up sign-in so linking takes one click, then remind people from Needs a look.',
  setUpSignIn: 'Set up sign-in',
} as const;
